import "./App.css";

import React, { useState, useEffect, useMemo } from "react";

import Typography from "@mui/material/Typography";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ErrorIcon from "@mui/icons-material/Error";
import SendIcon from "@mui/icons-material/Send";

import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Button from "@mui/material/Button";
import { MuiChipsInput } from "mui-chips-input";
import * as lodash from "lodash";

import * as riki from "jsriki";

import {
  activeLabelsState,
  projectPickerState,
  themeModeState,
} from "./JotaiAtoms";
import { reloadState } from "./JotaiAtoms";
import type { ThemeMode } from "./themeConfig";
import { StyledTooltip, StyledTooltipContent } from "./themeConfig";

import { useAtom, useAtomValue, useSetAtom } from "jotai";

import log from "./logger";

import * as u from "./utility";

import { DEFAULT_FONT_SIZE } from "./constants";
import {
  useHttpPostAndProject,
  useHookUrl,
  projectSettingsAtom,
  httpPostAtom,
  ProjectSettings,
} from "./hooks";
import { READ_ONLY_CONF } from "./constants";
import Divider from "@mui/material/Divider";

import { Theme, useTheme } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { getCurrentUserEmail } from "./Auth";

import { hsvaToHex, hexToHsva, HsvaColor } from "@uiw/react-color";

type UserRole = {
  project: string;
  role: RoleName;
  email: string;
  created_at?: string;
};

function DangerousZone({ roles }: { roles: UserRole[] }) {
  const [confirmation, setConfirmation] = useState("");
  const [statement, setStatement] = useState("");
  const [httpPost, project] = useHttpPostAndProject();
  const [currentUser, setCurrentUser] = useState<string>();

  useEffect(() => {
    getCurrentUserEmail()
      .then((email: string) => setCurrentUser(email))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (project) {
      setStatement(`permanently delete ${project}`);
    } else {
      setStatement("");
    }
  }, [project]);

  const isCurrentUserOwner = () => {
    const owner = roles.find((e) => e.role === "owner");
    if (!owner) {
      return false;
    }

    return owner.email === currentUser;
  };

  const handleDelete = () => {
    const request = async () => {
      try {
        await httpPost("admin", { action: "revoke" });
        window.location.href = u.getHomeUrl();
      } catch (err) {}
    };

    if (statement && confirmation === statement) {
      setConfirmation("");
      request();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmation(e.target.value);
  };

  return (
    <>
      {isCurrentUserOwner() && (
        <Box
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 100, 100, 0.08)"
                : "rgba(255, 100, 100, 0.06)",
            border: (theme) =>
              `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255, 100, 100, 0.2)"
                  : "rgba(255, 100, 100, 0.15)"
              }`,
            borderRadius: "8px",
          }}
        >
          <Typography
            sx={{
              color: "var(--status-old)",
              fontWeight: 500,
              fontSize: "15px",
              mb: 2,
            }}
          >
            Danger Zone
          </Typography>
          <Stack direction={"row"} gap={1}>
            <TextField
              label={statement}
              value={confirmation}
              onChange={handleChange}
              size="small"
              sx={{ fontSize: "13px" }}
            />
            <Button
              onClick={handleDelete}
              disabled={confirmation !== statement}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontSize: "13px",
                borderColor: "var(--status-old)",
                color: "var(--status-old)",
                "&:hover": {
                  borderColor: "var(--status-old)",
                  backgroundColor: "rgba(255, 100, 100, 0.08)",
                },
              }}
            >
              Delete project
            </Button>
          </Stack>
        </Box>
      )}
    </>
  );
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

function getStyles(name: string, personName: string[], theme: Theme) {
  return {
    fontWeight: personName.includes(name)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

export default function SelectDropdown({
  current,
  options,
  label,
  fontSize,
  onChange,
}: {
  current: string;
  options: string[];
  label: string;
  fontSize?: number | string;
  onChange?: (value: string) => void;
}) {
  const theme = useTheme();
  const [option, setOption] = React.useState<string>(current);

  const handleChange = (event: SelectChangeEvent<string>) => {
    setOption(event.target.value);
    if (onChange) {
      onChange(event.target.value);
    }
  };

  return (
    <FormControl sx={{ m: 0, minWidth: 100 }}>
      <Select
        labelId="demo-multiple-name-label"
        value={option}
        onChange={handleChange}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
              width: 120,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#1f1f1f" : "#ffffff",
              border: (theme) =>
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.08)"
                  : "1px solid rgba(0, 0, 0, 0.08)",
              borderRadius: "12px",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 4px 20px rgba(0, 0, 0, 0.5)"
                  : "0 4px 20px rgba(0, 0, 0, 0.1)",
              mt: 0.5,
            },
          },
        }}
        variant="standard"
        sx={{
          fontSize,
          color: "var(--text-primary)",
          fontWeight: 400,
          "&:before": {
            borderBottom: "none",
          },
          "&:after": {
            borderBottom: "none",
          },
          "&:hover:not(.Mui-disabled):before": {
            borderBottom: "none",
          },
          "&.Mui-focused": {
            "& .MuiSelect-select": {
              backgroundColor: "transparent",
            },
          },
          "& .MuiSelect-select": {
            paddingLeft: 0,
            paddingRight: "24px !important",
            "&:focus": {
              backgroundColor: "transparent",
            },
          },
          "& .MuiSvgIcon-root": {
            color: "var(--text-tertiary)",
            fontSize: "18px",
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option}
            value={option}
            sx={{
              fontSize,
              color: "var(--text-primary)",
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.04)",
              },
              "&.Mui-selected": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.08)",
                "&:hover": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.12)"
                      : "rgba(0, 0, 0, 0.12)",
                },
              },
            }}
          >
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const ROLES = ["admin", "viewer", "owner"] as const;
type RoleName = (typeof ROLES)[number];

function TeamMembers({ roles }: { roles: UserRole[] }) {
  const [httpPost, project] = useHttpPostAndProject();
  const [rolesCopy, setRoles] = useState<UserRole[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    setRoles([...roles]);
  }, [roles]);

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validEmails = useMemo(() => {
    return newEmail
      .split(/\s+/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0 && isValidEmail(e));
  }, [newEmail]);

  const newEmails = useMemo(() => {
    return validEmails.filter(
      (email) => !rolesCopy.find((role) => role.email === email)
    );
  }, [validEmails, rolesCopy]);

  const grantRequest = async (action: string, email: string, role?: string) => {
    try {
      await httpPost("admin", { action: action, email: email, role });
    } catch (err) {}
  };

  const handleInvite = async () => {
    if (newEmails.length === 0) return;

    setInviting(true);

    try {
      // Invite only new emails
      for (const email of newEmails) {
        await grantRequest("grant", email, "admin");
        setRoles((prev: UserRole[]) => {
          return [...prev, { project, email: email, role: "admin" }];
        });
      }
      setNewEmail("");
    } catch (err) {
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvite = async (email: string) => {
    try {
      await grantRequest("grant", email, "admin");
    } catch (err) {}
  };

  const handleRemove = (email: string) => {
    grantRequest("revoke", email)
      .then(() => {
        setRoles((prev: UserRole[]) => {
          const f = prev.findIndex((e) => e.email === email);
          if (f !== -1) {
            return [...prev.slice(0, f), ...prev.slice(f + 1)];
          }
          return prev;
        });
      })
      .catch(() => {});
  };

  const handleChangeRole = (userEmail: string, role: RoleName) => {
    grantRequest("grant", userEmail, role)
      .then(() => {
        setRoles((prev: UserRole[]) => {
          const f = prev.findIndex((e) => e.email === userEmail);
          if (f !== -1) {
            prev[f].role = role;
            return [...prev];
          }
          return prev;
        });
      })
      .catch(() => {});
  };

  return (
    <>
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "15px",
          mb: 2,
          color: "var(--text-primary)",
        }}
      >
        Team
      </Typography>

      {/* Team member list */}
      <Box
        sx={{
          mb: 3,
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(0, 0, 0, 0.2)"
              : "rgba(255, 255, 255, 0.5)",
          borderRadius: "6px",
          overflow: "hidden",
        }}
      >
        {rolesCopy.map((role, index) => (
          <Box
            key={role.email}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1.25,
              px: 2,
              borderBottom:
                index < rolesCopy.length - 1
                  ? (theme) =>
                      `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.06)"
                      }`
                  : "none",
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.02)"
                    : "rgba(0, 0, 0, 0.02)",
              },
            }}
          >
            <Stack direction="row" alignItems="center" gap={2} sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "var(--text-primary)",
                  minWidth: 200,
                }}
              >
                {role.email}
              </Typography>
              {role.role !== "owner" ? (
                <SelectDropdown
                  current={role.role}
                  options={[...ROLES].filter((e) => e !== "owner")}
                  label="Role"
                  fontSize={"13px"}
                  onChange={(newRole: string) =>
                    handleChangeRole(role.email, newRole as RoleName)
                  }
                />
              ) : (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {role.role}
                </Typography>
              )}
            </Stack>
            {role.role !== "owner" && (
              <Stack direction="row" gap={0.5}>
                <StyledTooltip title="Resend invitation">
                  <IconButton
                    size="small"
                    onClick={() => handleResendInvite(role.email)}
                    sx={{
                      color: "var(--text-tertiary)",
                      "&:hover": {
                        color: "var(--accent-primary)",
                      },
                    }}
                  >
                    <SendIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </StyledTooltip>
                <StyledTooltip title="Remove member">
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(role.email)}
                    sx={{
                      color: "var(--text-tertiary)",
                      "&:hover": {
                        color: "var(--status-old)",
                      },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </StyledTooltip>
              </Stack>
            )}
          </Box>
        ))}
      </Box>

      {/* Add member section */}
      <Box>
        <Typography
          sx={{
            fontSize: "13px",
            mb: 1.5,
            color: "var(--text-secondary)",
          }}
        >
          Invite new members
        </Typography>
        <Stack direction="row" gap={1}>
          <TextField
            placeholder="Email address (space-separated for multiple)"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleInvite();
              }
            }}
            size="small"
            sx={{
              flex: 1,
              maxWidth: 400,
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
              },
            }}
          />
          <Button
            onClick={handleInvite}
            disabled={newEmails.length === 0 || inviting}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontSize: "13px",
              px: 2,
            }}
          >
            {newEmails.length > 1 ? `Invite ${newEmails.length}` : "Invite"}
          </Button>
        </Stack>
      </Box>
    </>
  );
}

type KeyValueConfigProps = {
  items: {
    name: string;
    value: string;
    editable?: boolean;
    warn?: string;
    health?: ConfigHealth;
    replaceable?: boolean;
  }[];
  onCreate: (name: string, value: string) => void;
  onEdit: (name: string, value: string, index: number) => void;
  onDelete?: (index: number) => void;
};

function KeyValueConfig({
  items,
  onCreate,
  onEdit,
  onDelete,
}: KeyValueConfigProps) {
  const [generation, setGeneration] = useState(0);
  const [newRowKey, setNewRowKey] = useState(0);

  const handleDelete = (index: number) => {
    setGeneration((prev) => prev + 1);
    if (onDelete) {
      onDelete(index);
    }
  };

  const isDuplicateOrEmpty = (key: string, index?: number) => {
    if (!key) {
      return true;
    }

    for (let i = 0; i < items.length; ++i) {
      if (i !== index && key === items[i].name) {
        return true;
      }
    }

    return false;
  };

  const handleEdit = (key: string, value: string, index: number) => {
    if (isDuplicateOrEmpty(key, index)) {
      return false;
    }
    const allowed = onEdit(key, value, index);
    if (allowed === undefined || allowed) {
      return true;
    }

    return false;
  };

  const handleCreate = (key: string, value: string) => {
    if (isDuplicateOrEmpty(key)) {
      return false;
    }
    if (value) {
      onCreate(key, value);
      setNewRowKey((prev) => prev + 1);
    }
    return true;
  };

  return (
    <Box
      sx={{
        backgroundColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(0, 0, 0, 0.2)"
            : "rgba(255, 255, 255, 0.5)",
        borderRadius: "6px",
        overflow: "hidden",
      }}
    >
      {items &&
        items.map((row, index) => (
          <ConfigLine
            key={`${generation}:${index}`}
            initKey={row.name}
            initValue={row.value}
            editable={row.editable === undefined || row.editable}
            onEdit={(k, v) => handleEdit(k, v, index)}
            onDelete={onDelete ? () => handleDelete(index) : undefined}
            warn={row.warn}
            health={row.health}
            replaceable={row.replaceable}
            isLast={index === items.length - 1}
          />
        ))}
      {items && (
        <ConfigLine
          key={`new-row-${newRowKey}`}
          initKey=""
          initValue=""
          editable={true}
          onEdit={handleCreate}
          isLast={true}
        />
      )}
    </Box>
  );
}

function ProjectConfig() {
  type ConfigData = {
    name: string;
    value: string;
    editable?: boolean;
    warn?: string;
    replaceable?: boolean;
    health?: ConfigHealth;
  };

  const [config, setConfig] = useState<ConfigData[]>([]);
  const [httpPost] = useHttpPostAndProject();
  const settings = useAtomValue(projectSettingsAtom);
  const setSettings = useSetAtom(projectSettingsAtom);

  // const pendingUpdate = (key: string, value: string) => {
  //   setTimeout(() => {
  //     getData();
  //   }, 2000);
  // };

  const handleEdit = async (key: string, value: string, index?: number) => {
    const request = async (
      key: string,
      value: string
    ): Promise<undefined | { data: any }> => {
      try {
        return await httpPost("admin", {
          action: "config",
          key: key,
          value: value,
        });
      } catch (err) {
        return undefined;
      } finally {
      }
    };

    let ok: undefined | { data: any } = undefined;
    if (index !== undefined) {
      if (config[index].name !== key || config[index].value !== value) {
        let newConfig = [...config];
        newConfig[index] = {
          name: key,
          value: value,
        };
        setConfig(newConfig);
        ok = await request(key, value);
        if (!ok) {
          setConfig((prev: ConfigData[]) => {
            const found = prev.findIndex(
              (e) => e.name == key && e.value === value
            );
            if (found !== -1) {
              const next = [...prev];
              prev[found].warn = "Something is wrong, perhaps a bad name.";
              return next;
            } else {
              return prev;
            }
          });
        }
        if (ok) {
          // rename or delete a previously existing config
          if (key !== config[index].name) {
            ok = await request(config[index].name, "");
          }
        }
      }
    } else {
      // create a new config
      ok = await request(key, value);
    }

    if (ok) {
      // pendingUpdate(key, value);
      mergeConfigs(ok.data.config);
      healthCheck();
    }

    return ok;
  };

  const handleNewConfig = async (key: string, value: string) => {
    setConfig([
      ...config,
      {
        name: key,
        value: value,
      },
    ]);
    if (key && value) {
      const ok = await handleEdit(key, value);
      if (!ok) {
        setConfig((prev: ConfigData[]) => {
          const found = prev.findIndex(
            (e) => e.name == key && e.value === value
          );
          if (found !== -1) {
            const next = [...prev];
            prev[found].warn = "Something is wrong, perhaps a bad name.";
            return next;
          } else {
            return prev;
          }
        });
      }
    }
  };

  const handleDelete = (index: number) => {
    const itemName = config[index].name;

    const request = async () => {
      try {
        await httpPost("admin", {
          action: "config",
          key: itemName,
          value: "",
        });

        // Update the atom to remove the deleted key
        setSettings((prev) => {
          if (prev && prev.config) {
            const newConfig = { ...prev.config };
            delete newConfig[itemName];
            return { ...prev, config: newConfig };
          }
          return prev;
        });
      } catch (err) {
        log.error("Failed to delete config:", err);
      } finally {
      }
    };
    request();

    let newConfig = config.slice(0, index).concat(config.slice(index + 1));
    setConfig(newConfig);
  };

  const mergeConfigs = (inputConfig: any) => {
    let conf = [];
    for (let [k, v] of Object.entries(inputConfig)) {
      if (k !== "SK" && k !== "PK" && k !== "original_owner") {
        conf.push({
          name: k,
          value: v as string,
          editable: !READ_ONLY_CONF.includes(k),
          replaceable: k.includes("secret") || k.includes("token"),
        });
      }
    }
    const order = [...READ_ONLY_CONF, "slack", "github", "gitlab"];
    let reordered: ConfigData[] = [];
    let visited = new Set();
    for (let o of order) {
      for (let c of conf) {
        if (c.name.startsWith(o)) {
          reordered.push(c);
          visited.add(c.name);
        }
      }
    }
    for (let c of conf) {
      if (!visited.has(c.name)) {
        reordered.push(c);
      }
    }

    // merge configs
    setConfig((prev) => {
      if (prev.length === 0) {
        if (reordered.length === 0) {
          return prev;
        } else {
          return reordered;
        }
      } else {
        const out: ConfigData[] = [];
        prev.forEach((e) => {
          const found = reordered.find((x) => e.name === x.name);
          if (found) {
            out.push({ ...found, health: e.health });
          } else if (e.warn) {
            out.push(e);
          }
        });
        reordered.forEach((e) => {
          const found = out.find((x) => e.name === x.name);
          if (!found) {
            out.push(e);
          }
        });
        return out;
      }
    });
  };

  const updateHealthStatus = (healthStatus: ConfigHealth[]) => {
    setConfig((prev: ConfigData[]) => {
      const newConfig = [...prev];
      newConfig.forEach((e) => {
        e.health = undefined;
      });

      for (const health of healthStatus) {
        const configName = health.name;
        const f = newConfig.find((e) => e.name == configName);
        if (f) {
          f.health = health;
        }
      }

      return newConfig;
    });
  };

  const healthCheck = () => {
    const request = async () => {
      try {
        const response = await httpPost("admin", { action: "healthcheck" });
        updateHealthStatus(response.data);
      } catch (err) {
        log.debug(err);
      } finally {
      }
    };
    request();
  };

  useEffect(() => {
    if (settings) {
      mergeConfigs(settings.config);
      healthCheck();
    }
  }, [settings]);

  return (
    <Stack direction={"column"}>
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "15px",
          mb: 2,
          color: "var(--text-primary)",
        }}
      >
        Configuration
      </Typography>
      <KeyValueConfig
        items={config}
        onCreate={handleNewConfig}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </Stack>
  );
}

function Workflow() {
  const [workflow, setWorkflow] = useState<string>();
  const [error, setError] = useState(false);
  const activeLabels = useAtomValue(activeLabelsState);
  const [dirty, setDirty] = useState(false);
  const [focused, setFocused] = useState(false);
  const settings = useAtomValue(projectSettingsAtom);
  const [httpPost] = useHttpPostAndProject();

  useEffect(() => {
    if (settings) {
      setWorkflow(JSON.stringify(settings.workflow, null, 2));
      setError(false);
    }
  }, [settings]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWorkflow(e.target.value || " ");
    setDirty(true);
  };

  const handleSave = () => {
    const request = async (workflow: any) => {
      try {
        await httpPost("admin", {
          action: "setworkflow",
          workflow: workflow,
        });
      } catch (err) {}
    };

    try {
      let parsed: { states: { [key: string]: string[] } } =
        JSON.parse('{"states":{}}');
      if (workflow && workflow.trim()) {
        parsed = JSON.parse(workflow);
      }

      let display: { [key: string]: string } = {};
      if (activeLabels) {
        for (let [k, v] of Object.entries(activeLabels)) {
          const d = v.display;
          if (typeof d === "string") {
            display[d] = k;
          }
        }
      }

      let fixedWorkflow: { states: { [key: string]: string[] } } = {
        states: {},
      };
      for (let [k, v] of Object.entries(parsed.states)) {
        let fixed: string[] = [];
        for (let label of v) {
          if (display[label] && (!activeLabels || !activeLabels[label])) {
            fixed.push(display[label]);
          } else {
            fixed.push(label);
          }
        }
        fixedWorkflow.states[k] = fixed;
      }

      setWorkflow(JSON.stringify(fixedWorkflow, null, 2));
      request(fixedWorkflow);
      setError(false);
      setDirty(false);
    } catch (e) {
      setError(true);
    }
  };

  const handleCancel = () => {
    if (settings) {
      setWorkflow(JSON.stringify(settings.workflow, null, 2));
      setDirty(false);
    }
  };

  return (
    <Stack direction={"column"}>
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "15px",
          mb: 2,
          color: "var(--text-primary)",
        }}
      >
        Workflow
      </Typography>
      <TextField
        error={error}
        fullWidth
        size="small"
        value={workflow || " "}
        multiline
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        variant="outlined"
        placeholder="Workflow configuration (JSON)"
        inputProps={{
          style: {
            fontSize: "13px",
            fontFamily: "monospace",
            lineHeight: 1.5,
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            color: "var(--text-primary)",
            "& fieldset": {
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.12)"
                  : "rgba(0, 0, 0, 0.12)",
            },
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Stack direction={"row"} gap={1}>
                <Button
                  onClick={handleSave}
                  disabled={!dirty}
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontSize: "13px",
                  }}
                >
                  Save
                </Button>
                <Button
                  onClick={handleCancel}
                  disabled={!dirty}
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontSize: "13px",
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </InputAdornment>
          ),
        }}
      />
    </Stack>
  );
}

function ThemeSelector() {
  const [themeMode, setThemeMode] = useAtom(themeModeState);

  const handleChange = (event: SelectChangeEvent<string>) => {
    setThemeMode(event.target.value as "light" | "dark" | "system");
  };

  return (
    <>
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "15px",
          mb: 2,
          color: "var(--text-primary)",
        }}
      >
        Appearance
      </Typography>
      <Stack direction="row" alignItems="center" gap={2}>
        <Typography
          sx={{
            fontSize: "13px",
            minWidth: 80,
            color: "var(--text-secondary)",
          }}
        >
          Theme:
        </Typography>
        <FormControl sx={{ minWidth: 150 }}>
          <Select
            value={themeMode}
            onChange={handleChange}
            size="small"
            sx={{
              fontSize: "13px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.12)"
                    : "rgba(0, 0, 0, 0.12)",
              },
            }}
          >
            <MenuItem value="light" sx={{ fontSize: "13px" }}>
              Light
            </MenuItem>
            <MenuItem value="dark" sx={{ fontSize: "13px" }}>
              Dark
            </MenuItem>
            <MenuItem value="system" sx={{ fontSize: "13px" }}>
              System
            </MenuItem>
          </Select>
        </FormControl>
        <Typography
          sx={{
            fontSize: "12px",
            color: "var(--text-tertiary)",
          }}
        >
          {themeMode === "system" && "(Following system preference)"}
        </Typography>
      </Stack>
    </>
  );
}

export function Settings() {
  const [denied, setDenied] = useState<"allowed" | "denied" | "unknown">(
    "unknown"
  );
  const hookUrl = useHookUrl();
  const [roles, setRoles] = useState<UserRole[]>([]);
  const settings = useAtomValue(projectSettingsAtom);
  const [version, setVersion] = useState<{
    version: string;
    buildTime: string;
  } | null>(null);

  useEffect(() => {
    if (settings) {
      setDenied("allowed");

      let roles = settings.roles;
      const owner = roles.findIndex((e: UserRole) => e.role === "owner");
      if (owner !== -1) {
        roles = [
          roles[owner],
          ...roles.slice(0, owner),
          ...roles.slice(owner + 1),
        ];
      }
      setRoles(roles);
    } else {
      setDenied("denied");
    }
  }, [settings]);

  useEffect(() => {
    // Fetch version info
    fetch("/version.json")
      .then((response) => response.json())
      .then((data) => setVersion(data))
      .catch(() => setVersion(null));
  }, []);

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(hookUrl);
  };

  return (
    <>
      <ProjectSettingsData />
      {denied === "allowed" && (
        <Stack maxWidth={900} direction="column" sx={{ mt: 1.5, ml: 2 }}>
          {/* Webhook URL - special box */}
          <Box
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.02)"
                  : "rgba(0, 0, 0, 0.02)",
              border: (theme) =>
                `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.08)"
                }`,
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                mb: 1,
                color: "var(--text-secondary)",
              }}
            >
              Webhook URL
            </Typography>
            <Stack direction={"row"} alignItems={"center"} gap={1}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontFamily: "monospace",
                  color: "var(--text-primary)",
                }}
              >
                {hookUrl}
              </Typography>
              <StyledTooltip title="Copy URL">
                <IconButton
                  size="small"
                  onClick={handleCopyUrl}
                  sx={{
                    color: "var(--text-secondary)",
                    "&:hover": {
                      color: "var(--accent-primary)",
                    },
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </StyledTooltip>
            </Stack>
          </Box>

          {/* Project Configuration */}
          <Box sx={{ mb: 4 }}>
            <ProjectConfig />
          </Box>

          {/* Workflow */}
          <Box sx={{ mb: 4 }}>
            <Workflow />
          </Box>

          {/* User Queries */}
          <Box sx={{ mb: 4 }}>
            <UserQueries />
          </Box>

          {/* Team Members */}
          <Box sx={{ mb: 6 }}>
            <TeamMembers roles={roles} />
          </Box>

          {/* Theme */}
          <Box sx={{ mb: 4 }}>
            <ThemeSelector />
          </Box>

          {/* Danger Zone - special box */}
          <DangerousZone roles={roles} />

          {/* Version */}
          {version && (
            <Box sx={{ mt: 2, mb: 4, textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "var(--text-tertiary)",
                  fontFamily: "monospace",
                }}
              >
                v{version.version} • Built{" "}
                {new Date(version.buildTime).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Stack>
      )}
    </>
  );
}

type ConfigUsage = {
  total: number;
  err?: number;
  ok?: number;
  last_ok?: string;
  last_err?: string;
};

type ResourceUsage = {
  last_seen: string;
  num_seen: number;
  name: string;
  disabled?: boolean;
};

type ConfigHealth = {
  status: "ok" | "err";
  name: string;
  usage?: {
    [key: string]: ConfigUsage;
  };
  resources?: {
    [key: string]: ResourceUsage;
  };
};

type ConfigLineProps = {
  initKey: string;
  initValue: string;
  onEdit: (name: string, value: string) => boolean;
  editable: boolean;
  onDelete?: () => void;
  warn?: string;
  health?: ConfigHealth;
  replaceable?: boolean;
  isLast?: boolean;
};

const createHealthReport = (health: ConfigHealth, theme: Theme) => {
  type Report = {
    errorRate?: number;
    successRate?: number;
    url: string;
    lastSuccess?: string;
    lastError?: string;
    errorCodes: string[];
  };

  type ResourceReport = {
    name: string;
    ts: string;
    disabled?: boolean;
  };

  let report: Report[] = [];

  const successTimestamps = Object.values(health.usage!)
    .map((usage) => usage.last_ok)
    .filter((e) => e !== undefined)
    .sort()
    .reverse() as string[];
  const errorTimestamps = Object.values(health.usage!)
    .map((usage) => usage.last_err)
    .filter((e) => e !== undefined)
    .sort()
    .reverse() as string[];

  Object.entries(health.usage!)
    .sort((a, b) => b[1].total - a[1].total)
    .forEach(([url, usage]) => {
      const errorRate = usage.err
        ? Math.round((10000 * usage.err) / usage.total)
        : undefined;

      const successRate = usage.ok
        ? Math.round((10000 * usage.ok) / usage.total)
        : undefined;

      const errorCodes = Object.entries(usage)
        .filter(([key, value]) => key.startsWith("status_"))
        .sort((a, b) => (b[1] as number) - (a[1] as number))
        .map((e) => e[0].slice(7))
        .filter((e) => e !== "200");

      const lastSuccess = usage.last_ok;
      const lastError = usage.last_err;
      report.push({
        errorRate,
        successRate,
        url,
        lastSuccess,
        lastError,
        errorCodes,
      });
    });

  const saturate = (
    base_color: string,
    all_timestamps: string[],
    ts?: string
  ) => {
    if (!ts) {
      return base_color;
    }

    const hsva = hexToHsva(base_color);
    const rank =
      (all_timestamps.length - all_timestamps.findIndex((e) => e === ts)) /
      all_timestamps.length;
    hsva.s *= rank;
    return hsvaToHex(hsva);
  };

  const green = (last_ok: string | undefined, timestamps: string[]) => {
    // const lightgreen = '#90EE90';
    const green = "#00FF00";
    return saturate(green, timestamps, last_ok);
  };

  const red = (last_err?: string) => {
    // const red = '#FF0000';
    const tomato = "#FF6347";
    return saturate(tomato, errorTimestamps, last_err);
  };

  const blue = (last_ok: string | undefined, timestamps: string[]) => {
    return saturate("#87CEFA", timestamps, last_ok);
  };

  const successRate = (e: Report) => {
    return (
      <>
        {e.successRate !== undefined && e.successRate > 0 && (
          <span
            style={{
              color: green(e.lastSuccess, successTimestamps),
              fontWeight: "bold",
            }}
          >{`${e.successRate / 100}% `}</span>
        )}
      </>
    );
  };

  const errorRate = (e: Report) => {
    return (
      <>
        {e.errorRate !== undefined && e.errorRate > 0 && (
          <span style={{ color: red(e.lastError), fontWeight: "bold" }}>{`${
            e.errorRate / 100
          }% `}</span>
        )}
      </>
    );
  };

  let resources: ResourceReport[] = [];
  let resourceTimestamps: string[] = [];

  if (health.resources) {
    resourceTimestamps = Object.values(health.resources)
      .map((usage) => usage.last_seen)
      .sort()
      .reverse();
    resources = Object.values(health.resources)
      .map((e) => {
        return { ...e, num_seen: e.disabled ? -1 : e.num_seen };
      })
      .sort((a, b) => b.num_seen - a.num_seen)
      .map((e) => {
        return { name: e.name, ts: e.last_seen, disabled: e.disabled };
      });
  }

  return (
    <StyledTooltipContent isDark={theme.palette.mode === "dark"}>
      {report.map((e) => {
        return (
          <div key={e.url}>
            {e.lastError && e.lastSuccess && e.lastError > e.lastSuccess ? (
              <>
                {errorRate(e)}
                {successRate(e)}
              </>
            ) : (
              <>
                {successRate(e)}
                {errorRate(e)}
              </>
            )}
            {e.url}
            {e.errorCodes.map((code) => {
              return (
                <span key={code} style={{ color: "tomato" }}>
                  {` ${code}`}
                </span>
              );
            })}
          </div>
        );
      })}
      {resources.length > 0 && (
        <>
          <Divider sx={{ borderColor: "silver", m: 1 }} />
          {resources.map((e) => {
            return (
              <Box
                key={e.name}
                component={"span"}
                sx={{
                  color: !e.disabled
                    ? blue(e.ts, resourceTimestamps)
                    : "#a0a0a0",
                  m: 0.2,
                }}
              >
                {`${e.name} `}
              </Box>
            );
          })}
        </>
      )}
    </StyledTooltipContent>
  );
};

function HealthStatusIcon({ health }: { health?: ConfigHealth }) {
  const theme = useTheme();

  return (
    <>
      {health?.status === "ok" && (
        <>
          {health.usage && (
            <StyledTooltip
              richContent
              title={createHealthReport(health, theme)}
              slotProps={{
                tooltip: { sx: { maxWidth: "500px" } },
              }}
            >
              <CheckBoxIcon sx={{ fontSize: 16, color: "#4caf50" }} />
            </StyledTooltip>
          )}
          {!health.usage && (
            <CheckBoxIcon sx={{ fontSize: 16, color: "#4caf50" }} />
          )}
        </>
      )}

      {health?.status === "err" && (
        <>
          {health.usage && (
            <StyledTooltip
              richContent
              title={createHealthReport(health, theme)}
              slotProps={{
                tooltip: { sx: { maxWidth: "500px" } },
              }}
            >
              <ErrorIcon sx={{ fontSize: 16, color: "var(--status-old)" }} />
            </StyledTooltip>
          )}
          {!health.usage && (
            <ErrorIcon sx={{ fontSize: 16, color: "var(--status-old)" }} />
          )}
        </>
      )}
    </>
  );
}

function ConfigLine({
  initKey,
  initValue,
  onEdit,
  editable,
  onDelete,
  warn,
  health,
  replaceable,
  isLast,
}: ConfigLineProps) {
  const [editKey, setEditKey] = useState(false);
  const [editValue, setEditValue] = useState(false);
  const [key, setKey] = useState(initKey);
  const [value, setValue] = useState(initValue);
  const [focusValue, setFocusValue] = useState(true);
  const valueInputRef = React.useRef<HTMLInputElement>(null);

  const handleEditKey = () => {
    setEditKey(true);
    setEditValue(false);
  };

  const handleEditValue = () => {
    setEditValue(true);
    setEditKey(false);
    setTimeout(() => {
      valueInputRef.current?.focus();
    }, 0);
  };

  const handleEdit = (
    e: React.KeyboardEvent<HTMLDivElement>,
    field: string
  ) => {
    if (
      e.key === "Enter" ||
      (e.key === "Tab" && !replaceable && field === "value")
    ) {
      e.preventDefault();

      if (field === "key") {
        const currentKey = (e.target as any).value;
        setKey(currentKey);

        let editAllowed = currentKey.length > 0;
        if (currentKey !== initKey) {
          editAllowed = onEdit(currentKey, value);
        }

        if (editAllowed) {
          setEditKey(false);
          setFocusValue(true);
          setEditValue(true);
        }
      } else if (field === "value") {
        const currentValue = (e.target as any).value;
        if (onEdit(key, currentValue)) {
          setValue(currentValue);
          setFocusValue(false);
          setEditValue(false);
        }
      }
    } else if (
      e.key === "Escape" ||
      (e.key === "Tab" && !replaceable && field === "value")
    ) {
      if (field === "key") {
        setKey(initKey);
        setEditKey(false);
      } else {
        setValue(initValue);
        setEditValue(false);
      }
    }
  };

  const handleBlur = (e: any, field: string) => {
    if (field === "key") {
      const current = e.target.value;
      if (current && current !== initKey) {
        if (onEdit(current, value)) {
          setKey(current);
        } else {
          setKey(initKey);
        }
      }
      setEditKey(false);
    } else if (field === "value") {
      const current = e.target.value;
      if (current && current !== initValue) {
        if (onEdit(key, current)) {
          setValue(current);
        }
      }
      setEditValue(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        py: 1.25,
        px: 2,
        borderBottom: !isLast
          ? (theme) =>
              `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.06)"
              }`
          : "none",
        "&:hover": {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.02)"
              : "rgba(0, 0, 0, 0.02)",
        },
      }}
    >
      {/* Key column - 35% width */}
      <Box
        sx={{
          width: "35%",
          minWidth: 0,
          pr: 2,
          cursor: editable ? "pointer" : "default",
        }}
        onClick={editable && !editKey ? handleEditKey : undefined}
      >
        <Stack
          direction="row"
          gap={0.75}
          alignItems="center"
          sx={{
            position: "relative",
            "&::after":
              editable && !editKey
                ? {
                    content: '""',
                    position: "absolute",
                    bottom: -2,
                    left: 0,
                    right: 0,
                    height: "1px",
                    backgroundColor: "var(--accent-primary)",
                    opacity: 0,
                    transition: "opacity 0.2s",
                  }
                : {},
            "&:hover::after":
              editable && !editKey
                ? {
                    opacity: 0.3,
                  }
                : {},
          }}
        >
          {warn && (
            <StyledTooltip title={warn}>
              <WarningIcon
                sx={{ fontSize: 16, color: "orange", flexShrink: 0 }}
              />
            </StyledTooltip>
          )}

          <HealthStatusIcon health={health} />

          {!initKey && !initValue && (
            <AddIcon
              sx={{
                fontSize: 16,
                color: "var(--text-tertiary)",
                flexShrink: 0,
                mr: 0.25,
              }}
            />
          )}

          {!editKey && (
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                lineHeight: "20px",
                color: !editable
                  ? "var(--text-tertiary)"
                  : "var(--text-primary)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                minWidth: 0,
                padding: 0,
                margin: 0,
                border: 0,
              }}
            >
              {key || (
                <span
                  style={{ color: "var(--text-tertiary)", fontStyle: "italic" }}
                >
                  {!initKey && !initValue
                    ? "Click to add configuration..."
                    : ""}
                </span>
              )}
            </Typography>
          )}

          {editKey && (
            <TextField
              fullWidth
              autoFocus
              defaultValue={key}
              variant="standard"
              onKeyDown={(e) => handleEdit(e, "key")}
              onBlur={(e) => handleBlur(e, "key")}
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "var(--text-primary)",
                  padding: "0 !important",
                  margin: "0 !important",
                  border: 0,
                  "& input": {
                    padding: "0 !important",
                    margin: "0 !important",
                    lineHeight: "20px",
                    height: "20px",
                    border: 0,
                  },
                },
              }}
              sx={{
                margin: "0 !important",
                padding: "0 !important",
                flex: 1,
                minWidth: 0,
                border: 0,
                "& .MuiInput-root": {
                  marginTop: "0 !important",
                  padding: "0 !important",
                  border: 0,
                  "&:before": {
                    borderBottom: "1px solid var(--accent-primary)",
                    opacity: 0.3,
                  },
                  "&:after": {
                    borderBottom: "2px solid var(--accent-primary)",
                  },
                },
              }}
            />
          )}
        </Stack>
      </Box>

      {/* Value column - flexible width */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          pr: 1,
          cursor: editable && key ? "pointer" : "default",
          position: "relative",
          display: "flex",
          alignItems: "stretch",
          "&::after":
            editable && key && !editValue
              ? {
                  content: '""',
                  position: "absolute",
                  bottom: -2,
                  left: 0,
                  right: 0,
                  height: "1px",
                  backgroundColor: "var(--accent-primary)",
                  opacity: 0,
                  transition: "opacity 0.2s",
                }
              : {},
          "&:hover::after":
            editable && key && !editValue
              ? {
                  opacity: 0.3,
                }
              : {},
        }}
        onClick={editable && key ? handleEditValue : undefined}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: editValue ? "stretch" : "center",
            flex: 1,
            minWidth: 0,
            minHeight: "20px",
          }}
        >
          {!editValue && key && (
            <Typography
              sx={{
                fontSize: "13px",
                lineHeight: "20px",
                color: !editable
                  ? "var(--text-tertiary)"
                  : "var(--text-primary)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontFamily: replaceable ? "monospace" : "inherit",
                border: 0,
                padding: 0,
                margin: 0,
                flex: 1,
                minWidth: 0,
              }}
            >
              {replaceable && value ? "••••••••" : value}
            </Typography>
          )}
          {editValue && (
            <TextField
              fullWidth
              autoFocus
              inputRef={valueInputRef}
              defaultValue={!replaceable ? value : ""}
              variant="standard"
              focused={focusValue}
              onKeyDown={(e) => handleEdit(e, "value")}
              onBlur={(e) => handleBlur(e, "value")}
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: "13px",
                  color: "var(--text-primary)",
                  padding: "0 !important",
                  margin: "0 !important",
                  border: 0,
                  display: "flex",
                  alignItems: "flex-end",
                  height: "100%",
                  fontFamily: replaceable ? "monospace" : "inherit",
                  "& input": {
                    padding: "0 !important",
                    margin: "0 !important",
                    lineHeight: "20px",
                    height: "20px",
                    border: 0,
                  },
                },
              }}
              sx={{
                margin: "0 !important",
                padding: "0 !important",
                flex: 1,
                minWidth: 0,
                border: 0,
                height: "100%",
                display: "flex",
                "& .MuiInput-root": {
                  marginTop: "0 !important",
                  padding: "0 !important",
                  border: 0,
                  flex: 1,
                  "&:before": {
                    borderBottom: "1px solid var(--accent-primary)",
                    opacity: 0.3,
                  },
                  "&:after": {
                    borderBottom: "2px solid var(--accent-primary)",
                  },
                },
              }}
            />
          )}
        </Box>
      </Box>

      {/* Actions column - fixed width */}
      <Box
        sx={{
          width: "40px",
          display: "flex",
          justifyContent: "flex-end",
          flexShrink: 0,
        }}
      >
        {onDelete && editable && (
          <IconButton
            onClick={onDelete}
            size="small"
            sx={{
              color: "var(--text-tertiary)",
              "&:hover": {
                color: "var(--status-old)",
              },
            }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

function UserQueries() {
  const [items, setItems] = useState<
    { name: string; value: string; warn?: string }[]
  >([]);
  const [activeLabels, setActiveLabels] = useAtom(activeLabelsState);
  const [httpPost, project] = useHttpPostAndProject();
  const settings = useAtomValue(projectSettingsAtom);

  useEffect(() => {
    if (settings) {
      const sorted = settings.activelabels.queries.sort(
        (a: any, b: any) => Number(b.created) - Number(a.created)
      );
      const newItems = sorted.map((e: any) => {
        return { name: e.name, value: e.query };
      });
      setItems(newItems);
    }
  }, [settings]);

  const isShadowedByRegularLabel = (name: string) => {
    return activeLabels && activeLabels[name] && !activeLabels[name].expression
      ? true
      : false;
  };

  const getData = (project: string) => {
    if (project) {
      const request = async () => {
        try {
          const result = await httpPost("stories", {
            activelabels: true,
          });

          const sorted = result.data.activelabels.queries.sort(
            (a: any, b: any) => Number(b.created) - Number(a.created)
          );
          const newItems = sorted.map((e: any) => {
            return { name: e.name, value: e.query };
          });
          setItems(newItems);
        } catch (err) {}
      };
      request();
    }
  };

  const updateQuery = (project: string, name: string, expression: string) => {
    if (project) {
      const request = async () => {
        try {
          await httpPost("admin", {
            action: "updatequery",
            name: name,
            expression: expression,
          });

          setActiveLabels((prev) => {
            const preexisting = prev ? prev[name] : undefined;
            if (preexisting) {
              if (preexisting.expression) {
                let next = { ...prev };
                if (expression) {
                  next[name] = { ...preexisting, expression: expression };
                } else {
                  delete next[name];
                }
                return next;
              }
              return prev;
            } else {
              let next = { ...prev };
              next[name] = {
                name: name,
                display: name,
                expression: expression,
                index: prev ? Object.keys(prev).length : 0,
              };
              return next;
            }
          });
        } catch (err) {}
      };

      if (project) {
        request();
      }
    }
  };

  const handleCreate = (name: string, value: string) => {
    let newItems = [
      ...items,
      {
        name: name,
        value: value,
        warn: isShadowedByRegularLabel(name)
          ? "Shadowed by existing label with same name."
          : undefined,
      },
    ];
    setItems(newItems);
  };

  const handleEdit = (name: string, value: string, index: number) => {
    if (value && value.trim() && !riki.validate_expression(value.trim())) {
      return false;
    }

    let newItems = [...items];
    newItems[index] = {
      name: name,
      value: value,
      warn: isShadowedByRegularLabel(name)
        ? "Shadowed by existing label with same name."
        : undefined,
    };
    setItems(newItems);

    updateQuery(project, name, value);
  };

  const handleDelete = (index: number) => {
    updateQuery(project, items[index].name, "");

    let newItems = items.slice(0, index).concat(items.slice(index + 1));
    setItems(newItems);
  };

  return (
    <Stack direction={"column"}>
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "15px",
          mb: 2,
          color: "var(--text-primary)",
        }}
      >
        Custom Queries
      </Typography>
      <KeyValueConfig
        items={items}
        onCreate={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </Stack>
  );
}

function ProjectSettingsData({}) {
  const project = useAtomValue(projectPickerState);
  const reload = useAtomValue(reloadState);
  const httpPost = useAtomValue(httpPostAtom);
  const setSettings = useSetAtom(projectSettingsAtom);

  useEffect(() => {
    const request = async () => {
      try {
        const newSettings = await (
          await httpPost("admin", { action: "settings" })
        ).data;

        const dummy = {
          users: [],
          config: {},
          workflow: {},
          roles: [],
          activelabels: { channels: {}, labels: [], objects: {}, queries: [] },
        };
        setSettings((prev?: ProjectSettings) => {
          if (!prev || !lodash.isEqual(prev, newSettings)) {
            return newSettings;
          }
          return prev;
        });
      } catch (e) {}
    };
    if (project) {
      request();
    }
  }, [project, reload]);

  return null;
}
